/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useRef, useEffect } from "react";
import radioButtonActive from "@src/assets/svg/radio-button-active.svg";
import radioButtonInactive from "@src/assets/svg/radio-button-inactive.svg";
import searchIcon from "@src/assets/svg/magnifying-glass-gray.svg";
import caretDownIcon from "@src/assets/svg/caret-down.svg";
import styles from "./timezone-selector-with-search.module.scss";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import { useI18N, useLanguage } from "@src/language/language-context";
import { getTimezoneDisplayName } from "@src/cloud-connect/utils/timezone-display-utils";

const TIMEZONES = [
  // TODO: replace with dynamic list from backend or a library like moment-timezone
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Honolulu",
  "America/Halifax",
  "America/Toronto",
  "America/Vancouver",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "America/Lima",
  "America/Bogota",
  "America/Caracas",
  "America/Mexico_City",
  "Europe/London",
  "Europe/Dublin",
  "Europe/Lisbon",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Rome",
  "Europe/Amsterdam",
  "Europe/Warsaw",
  "Europe/Stockholm",
  "Europe/Helsinki",
  "Europe/Athens",
  "Europe/Bucharest",
  "Europe/Istanbul",
  "Europe/Moscow",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Africa/Lagos",
  "Africa/Nairobi",
  "Asia/Jerusalem",
  "Asia/Riyadh",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Dhaka",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Asia/Jakarta",
  "Asia/Tashkent",
  "Asia/Yekaterinburg",
  "Asia/Novosibirsk",
  "Asia/Krasnoyarsk",
  "Asia/Irkutsk",
  "Asia/Vladivostok",
  "Australia/Perth",
  "Australia/Adelaide",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Pacific/Auckland",
  "Pacific/Fiji",
  "Pacific/Guam",
] as const;

interface TimezoneSelectorProps {
  selectedTimezone: string;
  setSelectedTimezone: (timezone: string) => void;
}

const cssClassName = "timezone-selector-with-search";

export const TimezoneSelectorWithSearch = ({
  selectedTimezone,
  setSelectedTimezone,
}: TimezoneSelectorProps) => {
  const i18n = useI18N();
  const language = useLanguage();

  const texts = {
    timezoneLabel: i18n("modules.timezoneSelectorWithSearch.timezoneLabel"),
    searchPlaceholder: i18n(
      "modules.timezoneSelectorWithSearch.searchPlaceholder",
    ),
    noResults: i18n("modules.timezoneSelectorWithSearch.noResults"),
  };

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useClickOutsideListener(dropdownRef, () => setIsOpen(false), isOpen);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // dedupe by display name so visually identical labels are collapsed
  const uniqueTimezones = Array.from(
    new Map(
      TIMEZONES.map((tz) => [getTimezoneDisplayName(tz, language), tz]),
    ).values(),
  );

  const filteredTimezones = uniqueTimezones.filter((tz) =>
    getTimezoneDisplayName(tz, language)
      .toLowerCase()
      .includes(searchQuery.toLowerCase()),
  );

  const handleTimezoneSelect = (timezone: string) => {
    setSelectedTimezone(timezone);
    setIsOpen(false);
    setSearchQuery("");
  };

  return (
    <div className={styles[cssClassName]}>
      <p className={styles[`${cssClassName}__label`]}>{texts.timezoneLabel}</p>
      <div className={styles[`${cssClassName}__selector`]} ref={dropdownRef}>
        <div
          className={styles[`${cssClassName}__header`]}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={styles[`${cssClassName}__selected-timezone`]}>
            {selectedTimezone
              ? getTimezoneDisplayName(selectedTimezone, language)
              : getTimezoneDisplayName("UTC", language)}
          </span>
          <img
            className={styles[`${cssClassName}__caret`]}
            src={caretDownIcon}
            alt="Caret Down"
          />
        </div>

        {isOpen && (
          <div className={styles[`${cssClassName}__dropdown`]}>
            <div className={styles[`${cssClassName}__dropdown-inner`]}>
              <div className={styles[`${cssClassName}__search-container`]}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={texts.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles[`${cssClassName}__search-input`]}
                />
                <img
                  src={searchIcon}
                  alt="Search Icon"
                  className={styles[`${cssClassName}__search-icon`]}
                />
              </div>

              <div className={styles[`${cssClassName}__list`]}>
                {filteredTimezones.length > 0 ? (
                  filteredTimezones.map((timezone) => (
                    <div
                      key={timezone}
                      className={styles[`${cssClassName}__option`]}
                      onClick={() => handleTimezoneSelect(timezone)}
                    >
                      <div className={styles[`${cssClassName}__radio`]}>
                        <img
                          src={
                            selectedTimezone === timezone
                              ? radioButtonActive
                              : radioButtonInactive
                          }
                          alt="Timezone Radio Button"
                        />
                      </div>
                      <span className={styles[`${cssClassName}__radio-label`]}>
                        {getTimezoneDisplayName(timezone, language)}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className={styles[`${cssClassName}__no-results`]}>
                    {texts.noResults}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
